import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Car, Plus, Pencil, ImagePlus, X, ChevronLeft, ChevronRight, CheckCircle2, Link2, ShieldCheck, Tag, BadgeCheck, Search, Loader2, RefreshCw, Eye, ArrowLeft, Settings, BellRing, BellOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { vehiclesAPI, organizationsAPI, uploadAPI, vehicleSettingsAPI, vehicleBroadcastAPI } from '../lib/api';
import { RecipientListEditor } from '../components/shared/RecipientListEditor';
import { CurrencyInput, digitsOnly, formatMoney } from '../components/shared/CurrencyInput';
import { WALLPAPER_PRESETS, wallpaperBgStyle, hasWallpaper, cardSurfaceClass, CARD_STYLES } from '../components/shared/vehiclePresentation';
import { VehicleImg } from '../components/shared/VehicleImg';
import { useAuth } from '../lib/auth';
import VehicleShowroom from '../components/showroom/VehicleShowroom';
import { AlertsDeafBanner } from '../components/AlertsDeafBanner';

const BLANK = {
    stock_number: '', vin: '', make: '', model: '', year: '', trim: '', mileage: '', colour: '', asking_price: '',
    engine: '', drivetrain: '', transmission: '', fuel_type: '', fuel_economy: '', doors: '', seating: '', body_style: '',
    features: [],
    warranty_plans: [],
    test_drive_gift_enabled: null,
    field_notes: { mileage: '', colour: '', price: '', general: '' },
    photos: [], carfax_enabled: false, carfax_url: '',
    wallpaper_url: '', wallpaper_preset: '', card_style: 'frosted',
    special_offer_enabled: false, special_offer_url: '', special_offer_label: '',
    vin_source: null, vin_decoded_at: null,
};

const FEATURE_OPTIONS = [
    'AWD', '4WD', 'Leather', 'Heated Seats', 'Ventilated Seats', 'Heated Steering Wheel',
    'Sunroof', 'Panoramic Roof', 'Navigation', 'Apple CarPlay', 'Android Auto',
    'Blind Spot Monitoring', 'Adaptive Cruise Control', 'Lane Departure Warning',
    'Forward Collision Warning', 'Backup Camera', 'Parking Sensors', 'Premium Audio',
    'Remote Start', 'Power Liftgate', 'Trailer Tow Package', 'Off-Road Package',
    'Sport Package', 'Technology Package',
];

const STEPS = ['stock', 'model', 'details', 'photos', 'studio', 'carfax', 'card', 'review'];

export default function UsedVehicles() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, isSuperAdmin } = useAuth();
    const superAdmin = typeof isSuperAdmin === 'function' ? isSuperAdmin() : user?.role === 'super_admin';

    const [orgs, setOrgs] = useState([]);
    // Prefer the org passed in from Command Center (?org=), so the admin lands on
    // the same dealer they were viewing; fall back to their own default org.
    const [filterOrg, setFilterOrg] = useState(searchParams.get('org') || user?.organization_id || '');
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);

    // wizard state
    const [offerOpen, setOfferOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState('manual'); // guided | manual
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(BLANK);
    const [editing, setEditing] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [cards, setCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState('none');
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [reassignV, setReassignV] = useState(null);
    const [reassignCards, setReassignCards] = useState([]);
    const [reassignSel, setReassignSel] = useState('none');
    const [reassigning, setReassigning] = useState(false);
    const [vinStatus, setVinStatus] = useState(null); // {kind:'loading'|'found'|'partial'|'error'|'growth', msg}

    const toggleFeature = (f) => setForm((prev) => ({
        ...prev,
        features: prev.features.includes(f) ? prev.features.filter((x) => x !== f) : [...prev.features, f],
    }));

    const runVinLookup = async () => {
        const vin = (form.vin || '').replace(/[\s-]/g, '').toUpperCase();
        // Client-side VIN validation — 17 chars, no I/O/Q (VINs never contain those).
        // Catches typos before spending an Auto.dev API call.
        if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
            setVinStatus({ kind: 'error', msg: t('usedVehicles.vinInvalid', 'Enter a valid 17-character VIN (letters I, O and Q are not used).') });
            return;
        }
        setVinStatus({ kind: 'loading', msg: t('usedVehicles.vinLooking', 'Looking up vehicle information…') });
        try {
            const r = await vehiclesAPI.vinLookup(vin, filterOrg, !!editing);
            const f = r.data.fields || {};
            // Only populate EMPTY identity fields — never overwrite a value the dealer
            // already typed or corrected. Dealer-specific data (stock/mileage/price/
            // notes/photos) is never touched by a decode.
            setForm((prev) => ({
                ...prev,
                vin,
                make: prev.make || f.make || '',
                model: prev.model || f.model || '',
                year: prev.year || f.year || '',
                trim: prev.trim || f.trim || '',
                features: r.data.build_available && (r.data.features || []).length
                    ? Array.from(new Set([...(prev.features || []), ...r.data.features]))
                    : prev.features,
                vin_source: r.data.source || 'auto.dev',
                vin_decoded_at: r.data.fetched_at || new Date().toISOString(),
            }));
            const gotCore = f.make && f.model && f.year;
            if (!r.data.build_available) {
                setVinStatus({
                    kind: 'partial',
                    msg: gotCore
                        ? t('usedVehicles.vinFoundNoBuild', 'Vehicle information found. Detailed factory equipment isn’t available on the current plan — please select any Vehicle Features manually.')
                        : t('usedVehicles.vinPartial', 'We couldn’t auto-fill all fields — please review the populated fields and complete anything missing manually.'),
                });
            } else {
                setVinStatus({ kind: 'found', msg: t('usedVehicles.vinFound', 'Vehicle information found.') });
            }
        } catch (e) {
            const code = e?.response?.status;
            if (code === 404) setVinStatus({ kind: 'error', msg: t('usedVehicles.vinNotDecoded', 'We couldn’t decode this VIN. Please verify the VIN or enter the vehicle information manually.') });
            else if (code === 429) setVinStatus({ kind: 'error', msg: t('usedVehicles.vinRate', 'VIN lookup is busy right now — please try again shortly, or enter details manually.') });
            else setVinStatus({ kind: 'error', msg: t('usedVehicles.vinError', 'VIN lookup is unavailable right now. You can enter the vehicle information manually.') });
        }
    };

    useEffect(() => {
        organizationsAPI.list().then((r) => {
            const auto = (r.data || []).filter((o) => o.vertical === 'automotive');
            setOrgs(auto);
            if (!filterOrg && auto.length) setFilterOrg(auto[0].id);
        }).catch(() => {});
    }, []); // eslint-disable-line

    const loadVehicles = useCallback(async () => {
        if (!filterOrg) { setVehicles([]); return; }
        setLoading(true);
        try {
            const r = await vehiclesAPI.list(filterOrg);
            setVehicles(r.data || []);
        } catch { toast.error(t('usedVehicles.loadError', 'Could not load vehicles')); }
        finally { setLoading(false); }
    }, [filterOrg, t]);

    useEffect(() => { loadVehicles(); }, [loadVehicles]);

    // ?settings=1 — the "add a recipient" jump from the Command Center banner.
    // Waits for the org to resolve, then opens Showroom Settings once.
    const [deepLinkDone, setDeepLinkDone] = useState(false);
    useEffect(() => {
        if (deepLinkDone || searchParams.get('settings') !== '1' || !filterOrg) return;
        setDeepLinkDone(true);
        openSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOrg, searchParams, deepLinkDone]);

    const loadCards = useCallback(async (vehicleId) => {
        if (!filterOrg) return;
        try {
            const r = await vehiclesAPI.availableCards(filterOrg, vehicleId);
            setCards(r.data || []);
        } catch { setCards([]); }
    }, [filterOrg]);

    const openWizard = (guided) => {
        setMode(guided ? 'guided' : 'manual');
        setStep(0);
        setForm(BLANK);
        setVinStatus(null);
        setEditing(null);
        setSelectedCard('none');
        loadCards(null);
        setModalOpen(true);
    };

    const openEdit = async (v) => {
        setMode('manual');
        setStep(0);
        setVinStatus(null);
        setEditing(v);
        const fn = v.field_notes || {};
        setForm({
            stock_number: v.stock_number || '', vin: v.vin || '', make: v.make || '', model: v.model || '',
            year: v.year || '', trim: v.trim || '', mileage: v.mileage || '', colour: v.colour || '', asking_price: digitsOnly(v.asking_price),
            engine: v.engine || '', drivetrain: v.drivetrain || '', transmission: v.transmission || '', fuel_type: v.fuel_type || '', fuel_economy: v.fuel_economy || '', doors: v.doors || '', seating: v.seating || '', body_style: v.body_style || '',
            features: Array.isArray(v.features) ? v.features : [],
            warranty_plans: Array.isArray(v.warranty_plans) ? v.warranty_plans : [],
            test_drive_gift_enabled: v.test_drive_gift_enabled ?? null,
            field_notes: { mileage: fn.mileage || '', colour: fn.colour || '', price: fn.price || '', general: fn.general || '' },
            photos: v.photos || [], carfax_enabled: !!v.carfax_enabled, carfax_url: v.carfax_url || '',
            wallpaper_url: v.wallpaper_url || '', wallpaper_preset: v.wallpaper_preset || '', card_style: v.card_style || 'frosted',
            special_offer_enabled: !!v.special_offer_enabled, special_offer_url: v.special_offer_url || '', special_offer_label: v.special_offer_label || '',
            vin_source: v.vin_source || null, vin_decoded_at: v.vin_decoded_at || null,
        });
        setSelectedCard(v.assigned_card?.id || 'none');
        await loadCards(v.id);
        setModalOpen(true);
    };

    const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));
    const setPlan = (i, key, val) => setForm((f) => ({
        ...f,
        warranty_plans: (f.warranty_plans || []).map((p, x) => (x === i ? { ...p, [key]: val } : p)),
    }));

    const setNote = (k, val) => setForm((f) => ({ ...f, field_notes: { ...f.field_notes, [k]: val } }));

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = [];
            for (const file of files) {
                const r = await uploadAPI.uploadImage(file);
                if (r.data?.url) urls.push(r.data.url);
            }
            set('photos', [...form.photos, ...urls]);
        } catch { toast.error(t('usedVehicles.photoError', 'Photo upload failed')); }
        finally { setUploading(false); e.target.value = ''; }
    };

    const removePhoto = (i) => set('photos', form.photos.filter((_, idx) => idx !== i));

    const [wpUploading, setWpUploading] = useState(false);
    const onWallpaperUpload = async (e) => {
        const file = (e.target.files || [])[0];
        if (!file) return;
        setWpUploading(true);
        try {
            const r = await uploadAPI.uploadImage(file);
            if (r.data?.url) setForm((f) => ({ ...f, wallpaper_url: r.data.url, wallpaper_preset: '' }));
        } catch { toast.error(t('usedVehicles.photoError', 'Photo upload failed')); }
        finally { setWpUploading(false); e.target.value = ''; }
    };
    const pickPreset = (key) => setForm((f) => ({ ...f, wallpaper_preset: key, wallpaper_url: '' }));
    const clearWallpaper = () => setForm((f) => ({ ...f, wallpaper_url: '', wallpaper_preset: '' }));

    const stepValid = (id) => {
        if (id === 'stock') return !!form.stock_number.trim();
        if (id === 'model') return !!form.make.trim() && !!form.model.trim();
        if (id === 'carfax') return !form.carfax_enabled || /^https?:\/\//i.test(form.carfax_url.trim());
        return true;
    };

    const next = () => {
        const id = STEPS[step];
        if (!stepValid(id)) { toast.error(t('usedVehicles.fixStep', 'Please complete this step first')); return; }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };
    const back = () => setStep((s) => Math.max(s - 1, 0));

    const save = async () => {
        if (!form.stock_number.trim()) { toast.error(t('usedVehicles.stockRequired', 'Stock number is required')); return; }
        if (!form.make.trim() || !form.model.trim()) { toast.error(t('usedVehicles.makeModelRequired', 'Make and Model are required')); return; }
        if (form.carfax_enabled && !/^https?:\/\//i.test(form.carfax_url.trim())) { toast.error(t('usedVehicles.carfaxUrlRequired', 'A valid CARFAX URL is required')); return; }
        setSaving(true);
        try {
            const payload = {
                organization_id: filterOrg,
                stock_number: form.stock_number, vin: form.vin || null, make: form.make, model: form.model,
                year: form.year, trim: form.trim, mileage: form.mileage, colour: form.colour, asking_price: formatMoney(form.asking_price) || null,
                engine: form.engine || null, drivetrain: form.drivetrain || null, transmission: form.transmission || null, fuel_type: form.fuel_type || null, fuel_economy: form.fuel_economy || null, doors: form.doors || null, seating: form.seating || null, body_style: form.body_style || null,
                features: form.features || [],
                field_notes: form.field_notes, photos: form.photos,
                wallpaper_url: form.wallpaper_url || null, wallpaper_preset: form.wallpaper_preset || null, card_style: form.card_style || 'frosted',
                carfax_enabled: form.carfax_enabled, carfax_url: form.carfax_url,
                special_offer_enabled: form.special_offer_enabled, special_offer_url: form.special_offer_url, special_offer_label: form.special_offer_label,
                vin_source: form.vin_source, vin_decoded_at: form.vin_decoded_at,
                test_drive_gift_enabled: form.test_drive_gift_enabled,
                warranty_plans: (form.warranty_plans || [])
                    .filter((p) => (p.duration || '').trim() || (p.coverage || '').trim())
                    .map((p) => ({ duration: (p.duration || '').trim(), coverage: (p.coverage || '').trim(), notes: (p.notes || '').trim() })),
            };
            let vehicleId;
            if (editing) { await vehiclesAPI.update(editing.id, payload); vehicleId = editing.id; }
            else { const r = await vehiclesAPI.create(payload); vehicleId = r.data.id; }

            const prevCard = editing?.assigned_card?.id || null;
            const nextCard = selectedCard === 'none' ? null : selectedCard;
            if (nextCard && nextCard !== prevCard) await vehiclesAPI.assignCard(vehicleId, nextCard);
            else if (!nextCard && prevCard) await vehiclesAPI.unassignCard(vehicleId);

            toast.success(editing ? t('usedVehicles.saved', 'Vehicle saved') : t('usedVehicles.created', 'Vehicle added'));
            setModalOpen(false);
            loadVehicles();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('usedVehicles.saveError', 'Could not save vehicle'));
        } finally { setSaving(false); }
    };

    const toggleSold = async (v) => {
        try {
            if (v.status === 'active') await vehiclesAPI.markSold(v.id);
            else await vehiclesAPI.markActive(v.id);
            loadVehicles();
        } catch (err) { toast.error(err.response?.data?.detail || t('usedVehicles.soldError', 'Action failed')); }
    };

    const openReassign = async (v) => {
        setReassignV(v);
        setReassignSel(v.assigned_card?.id || 'none');
        try { const r = await vehiclesAPI.availableCards(filterOrg, v.id); setReassignCards(r.data || []); }
        catch { setReassignCards([]); }
    };
    const confirmReassign = async () => {
        const prev = reassignV.assigned_card?.id || null;
        const next = reassignSel === 'none' ? null : reassignSel;
        if (next === prev) { setReassignV(null); return; }
        setReassigning(true);
        try {
            if (next) await vehiclesAPI.assignCard(reassignV.id, next);
            else await vehiclesAPI.unassignCard(reassignV.id);
            toast.success(t('usedVehicles.tagReassigned', 'Tap Card updated'));
            setReassignV(null); loadVehicles();
        } catch (err) { toast.error(err.response?.data?.detail || t('usedVehicles.soldError', 'Action failed')); }
        finally { setReassigning(false); }
    };

    const coach = useMemo(() => ({
        stock: [t('usedVehicles.coach.stockT', 'Enter the stock number'), t('usedVehicles.coach.stockH', "Type the dealership stock number for this vehicle, e.g. B12345.")],
        model: [t('usedVehicles.coach.modelT', 'Make, model & year'), t('usedVehicles.coach.modelH', 'Tell customers exactly what this vehicle is.')],
        details: [t('usedVehicles.coach.detailsT', 'Mileage, colour & price'), t('usedVehicles.coach.detailsH', 'Add the key numbers. You can add a short customer note under each.')],
        photos: [t('usedVehicles.coach.photosT', 'Add photos'), t('usedVehicles.coach.photosH', 'Upload a few clear photos — exterior, interior, dashboard.')],
        studio: [t('usedVehicles.coach.studioT', 'Studio presentation'), t('usedVehicles.coach.studioH', 'Optional: choose a backdrop and frame style to make the main photo pop. Your photos stay untouched.')],
        carfax: [t('usedVehicles.coach.carfaxT', 'CARFAX'), t('usedVehicles.coach.carfaxH', 'Turn CARFAX on and paste the public CARFAX link, or leave it off.')],
        card: [t('usedVehicles.coach.cardT', 'Choose the Tap Card'), t('usedVehicles.coach.cardH', 'Select the physical Tap Card you are putting on this vehicle, e.g. UV-001.')],
        review: [t('usedVehicles.coach.reviewT', 'Review & save'), t('usedVehicles.coach.reviewH', 'Check everything, then save. You can edit anytime.')],
    }), [t]);

    // Showroom Settings (org-level dealer logo + e-transfer email)
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settings, setSettings] = useState({ dealer_logo_url: '', etransfer_email: '' });
    const [settingsBusy, setSettingsBusy] = useState(false);

    // ── Per-vehicle broadcast list ────────────────────────────────────────
    const [bc, setBc] = useState(null);      // {vehicle, rows, uses_default, muted, org_default, max}
    const [bcBusy, setBcBusy] = useState(false);
    const [bcPhonesOk, setBcPhonesOk] = useState(true);
    const [defPhonesOk, setDefPhonesOk] = useState(true);

    const openBroadcast = async (v) => {
        setBcBusy(true);
        try {
            const r = await vehicleBroadcastAPI.get(v.id);
            setBc({
                vehicle: v, rows: r.data.notification_phones || [],
                uses_default: r.data.uses_default, muted: r.data.muted,
                org_default: r.data.org_default || [], max: r.data.max_recipients || 10,
            });
        } catch (e) { toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong')); }
        finally { setBcBusy(false); }
    };

    const saveBroadcast = async () => {
        setBcBusy(true);
        try {
            await vehicleBroadcastAPI.update(bc.vehicle.id, {
                notification_phones: bc.uses_default ? null : bc.rows.filter((r) => (r.phone || '').trim()),
                use_default: bc.uses_default, muted: bc.muted,
            });
            toast.success(t('broadcast.saved', 'Alert list saved'));
            setBc(null);
            loadVehicles();
        } catch (e) { toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong')); }
        finally { setBcBusy(false); }
    };

    const bulkMute = async (muted) => {
        try {
            const r = await vehicleBroadcastAPI.bulkMute({ organization_id: filterOrg, all: true, muted });
            toast.success(muted
                ? t('broadcast.mutedAll', 'Alerts muted on {{n}} vehicles', { n: r.data.updated })
                : t('broadcast.unmutedAll', 'Alerts back on for {{n}} vehicles', { n: r.data.updated }));
            loadVehicles();
        } catch (e) { toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong')); }
    };

    const openSettings = async () => {
        setSettingsOpen(true);
        try {
            const r = await vehicleSettingsAPI.get(filterOrg);
            setSettings({
                dealer_logo_url: r.data?.dealer_logo_url || '', etransfer_email: r.data?.etransfer_email || '',
                broadcast_default: r.data?.broadcast_default || [],
                broadcast_template: r.data?.broadcast_template || {},
                max_recipients: r.data?.max_recipients || 10,
                buyer_theme: r.data?.buyer_theme || 'dark',
                fi_financing_enabled: r.data?.fi_financing_enabled !== false,
                fi_warranty_enabled: r.data?.fi_warranty_enabled !== false,
                fi_insurance_enabled: r.data?.fi_insurance_enabled !== false,
                fi_trade_in_enabled: r.data?.fi_trade_in_enabled !== false,
                action_noise_enabled: r.data?.action_noise_enabled !== false,
                // Default OFF, so an org that has never set it reads as off.
                payment_estimate_enabled: r.data?.payment_estimate_enabled === true,
                insurance_broker: r.data?.insurance_broker || { name: '', email: '', phone: '' },
                fi_trade_recipients: r.data?.fi_trade_recipients || [],
            });
        } catch { setSettings({ dealer_logo_url: '', etransfer_email: '', broadcast_default: [], broadcast_template: {} }); }
    };

    const onLogoUpload = async (e) => {
        const file = (e.target.files || [])[0];
        if (!file) return;
        setSettingsBusy(true);
        try {
            const r = await uploadAPI.uploadImage(file);
            if (r.data?.url) setSettings((s) => ({ ...s, dealer_logo_url: r.data.url }));
        } catch { toast.error(t('usedVehicles.photoError', 'Photo upload failed')); }
        finally { setSettingsBusy(false); e.target.value = ''; }
    };

    const saveSettings = async () => {
        setSettingsBusy(true);
        try {
            await vehicleSettingsAPI.update(filterOrg, {
                dealer_logo_url: settings.dealer_logo_url || null,
                etransfer_email: settings.etransfer_email || null,
                broadcast_default: (settings.broadcast_default || []).filter((r) => (r.phone || '').trim()),
                broadcast_template: settings.broadcast_template || null,
                buyer_theme: settings.buyer_theme || 'dark',
                fi_financing_enabled: !!settings.fi_financing_enabled,
                fi_warranty_enabled: !!settings.fi_warranty_enabled,
                fi_insurance_enabled: !!settings.fi_insurance_enabled,
                action_noise_enabled: !!settings.action_noise_enabled,
                payment_estimate_enabled: !!settings.payment_estimate_enabled,
                fi_trade_in_enabled: !!settings.fi_trade_in_enabled,
                insurance_broker: settings.insurance_broker || null,
                fi_trade_recipients: (settings.fi_trade_recipients || []).filter((r) => (r.phone || '').trim()),
            });
            toast.success(t('offerDeposit.settingsSaved', 'Showroom settings saved'));
            setSettingsOpen(false);
        } catch (err) { toast.error(err.response?.data?.detail || t('common.error', 'Something went wrong')); }
        finally { setSettingsBusy(false); }
    };

    const guided = mode === 'guided';
    const showSection = (id) => !guided || STEPS[step] === id;

    const orgName = (id) => orgs.find((o) => o.id === id)?.name || '—';

    return (
        <div className="space-y-6 pb-16" data-testid="used-vehicles-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <button type="button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/command-center'))}
                        data-testid="uv-back-btn"
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
                        <ArrowLeft className="w-4 h-4" />{t('common.back', 'Back')}
                    </button>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <Car className="w-6 h-6 text-amber-500" />{t('usedVehicles.title', 'Used Vehicles')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">{t('usedVehicles.subtitle', 'Manage the vehicles on your YulOnTap Tap Cards')}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {superAdmin && orgs.length > 1 && (
                        <Select value={filterOrg} onValueChange={setFilterOrg}>
                            <SelectTrigger className="w-56" data-testid="uv-org-filter"><SelectValue placeholder={t('usedVehicles.selectDealer', 'Select dealership')} /></SelectTrigger>
                            <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                        </Select>
                    )}
                    <Button variant="outline" onClick={() => bulkMute(true)} disabled={!filterOrg} data-testid="uv-mute-all-btn">
                        <BellOff className="w-4 h-4 mr-1.5" />{t('broadcast.muteAll', 'Mute all alerts')}
                    </Button>
                    <Button variant="outline" onClick={() => bulkMute(false)} disabled={!filterOrg} data-testid="uv-unmute-all-btn">
                        <BellRing className="w-4 h-4 mr-1.5" />{t('broadcast.unmuteAll', 'Unmute all')}
                    </Button>
                    <Button variant="outline" onClick={openSettings} disabled={!filterOrg} data-testid="uv-settings-btn">
                        <Settings className="w-4 h-4 mr-1.5" />{t('offerDeposit.settingsButton', 'Showroom Settings')}
                    </Button>
                    <Button onClick={() => setOfferOpen(true)} disabled={!filterOrg} data-testid="uv-add-btn">
                        <Plus className="w-4 h-4 mr-1.5" />{t('usedVehicles.add', 'Add Used Vehicle')}
                    </Button>
                </div>
            </div>

            {loading ? (
                <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
            ) : vehicles.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="uv-empty">
                    {t('usedVehicles.empty', 'No vehicles yet. Add your first used vehicle and assign a Tap Card to it.')}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {vehicles.map((v) => (
                        <div key={v.id} className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" data-testid={`uv-vehicle-card-${v.stock_number}`}>
                            <div className="aspect-video bg-muted relative flex items-center justify-center" style={wallpaperBgStyle(v) || undefined}>
                                {v.photos?.[0]
                                    ? (hasWallpaper(v)
                                        ? <div className={`m-3 rounded-lg overflow-hidden ${cardSurfaceClass(v.card_style, 'dark')} p-1`}><VehicleImg src={v.photos[0]} alt={`${v.make} ${v.model}`} imgClassName="max-h-28 w-auto object-contain rounded" placeholderClassName="w-full h-full" label={t('usedVehicles.imageUnavailable', 'Image unavailable')} iconClass="w-10 h-10" /></div>
                                        : <VehicleImg src={v.photos[0]} alt={`${v.make} ${v.model}`} imgClassName="w-full h-full object-cover" placeholderClassName="w-full h-full" label={t('usedVehicles.imageUnavailable', 'Image unavailable')} iconClass="w-10 h-10" />)
                                    : <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground" data-testid="vehicle-img-placeholder"><Car className="w-10 h-10 opacity-60" /><span className="text-[10px] opacity-70">{t('usedVehicles.imageUnavailable', 'Image unavailable')}</span></div>}
                                <span className={`absolute top-2 right-2 text-[11px] px-2 py-0.5 rounded-full font-medium ${v.status === 'sold' ? 'bg-red-500/90 text-white' : 'bg-emerald-500/90 text-white'}`}>
                                    {v.status === 'sold' ? t('usedVehicles.sold', 'Sold') : t('usedVehicles.active', 'Active')}
                                </span>
                            </div>
                            <div className="p-4 flex-1 flex flex-col gap-2">
                                <div className="flex items-baseline justify-between gap-2">
                                    <p className="font-semibold leading-tight text-foreground">{v.year} {v.make} {v.model}</p>
                                    <span className="text-sm font-bold text-amber-600 whitespace-nowrap">{v.asking_price || '—'}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">{t('usedVehicles.stockShort', 'Stock')} #{v.stock_number} · {v.mileage || '—'} · {v.colour || '—'}</p>
                                <div className="flex items-center gap-1.5 text-xs">
                                    <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
                                    {v.assigned_card
                                        ? <span className="text-foreground font-medium" data-testid={`uv-card-badge-${v.stock_number}`}>{v.assigned_card.card_id}</span>
                                        : <span className="text-amber-600">{t('usedVehicles.noCard', 'No Tap Card')}</span>}
                                    {v.carfax_enabled && <span className="ml-auto inline-flex items-center gap-1 text-emerald-600"><ShieldCheck className="w-3.5 h-3.5" />CARFAX</span>}
                                </div>
                                <div className="mt-auto pt-3 flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                        <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(v)} data-testid={`uv-edit-${v.stock_number}`}>
                                            <Pencil className="w-3.5 h-3.5 mr-1.5" />{t('common.edit', 'Edit')}
                                        </Button>
                                        <Button size="sm" variant={v.status === 'sold' ? 'secondary' : 'outline'} onClick={() => toggleSold(v)} data-testid={`uv-sold-${v.stock_number}`}>
                                            {v.status === 'sold' ? t('usedVehicles.reactivate', 'Reactivate') : t('usedVehicles.markSold', 'Mark Sold')}
                                        </Button>
                                    </div>
                                    <Button size="sm" variant="outline" className="w-full justify-center" onClick={() => openBroadcast(v)} data-testid={`uv-broadcast-${v.stock_number}`}>
                                        {v.broadcast_muted
                                            ? <><BellOff className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />{t('broadcast.mutedBadge', 'Alerts muted')}</>
                                            : <><BellRing className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />{t('broadcast.rowButton', 'Alert list')}</>}
                                    </Button>
                                    <Button size="sm" variant="ghost" className="w-full text-amber-600 hover:text-amber-700" onClick={() => openReassign(v)} data-testid={`uv-reassign-${v.stock_number}`}>
                                        <Link2 className="w-3.5 h-3.5 mr-1.5" />{t('usedVehicles.reassignTag', 'Reassign Tag')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Showroom Settings — dealer logo + e-transfer email (org-level) */}
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                <DialogContent className="sm:max-w-md" data-testid="uv-settings-modal">
                    <DialogHeader>
                        <DialogTitle>{t('offerDeposit.settingsTitle', 'Showroom Settings')}</DialogTitle>
                        <DialogDescription>{t('offerDeposit.settingsHint', "Your dealer logo appears in the buyer's showroom header. The e-transfer email is shown to buyers who leave a deposit.")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <AlertsDeafBanner organizationId={filterOrg} compact />
                        <div className="space-y-2">
                            <Label>{t('offerDeposit.settingsLogo', 'Dealer logo')}</Label>
                            <div className="flex items-center gap-3">
                                <div className="w-28 h-16 rounded-lg border border-border bg-muted flex items-center justify-center overflow-hidden" data-testid="uv-settings-logo-preview">
                                    {settings.dealer_logo_url
                                        ? <img src={settings.dealer_logo_url} alt="" className="max-h-14 max-w-24 object-contain" />
                                        : <ImagePlus className="w-5 h-5 text-muted-foreground" />}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="inline-flex items-center justify-center h-9 px-3 rounded-md border border-border text-sm cursor-pointer hover:bg-accent" data-testid="uv-settings-logo-upload">
                                        {settingsBusy ? t('usedVehicles.uploading', 'Uploading…') : t('offerDeposit.settingsLogoUpload', 'Upload logo')}
                                        <input type="file" accept="image/*" className="hidden" onChange={onLogoUpload} data-testid="uv-settings-logo-input" />
                                    </label>
                                    {settings.dealer_logo_url && (
                                        <Button size="sm" variant="ghost" onClick={() => setSettings((s) => ({ ...s, dealer_logo_url: '' }))} data-testid="uv-settings-logo-remove">
                                            <X className="w-3.5 h-3.5 mr-1" />{t('offerDeposit.settingsLogoRemove', 'Remove')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>{t('broadcast.defaultTitle', 'Default alert list for new vehicles')}</Label>
                            <p className="text-xs text-muted-foreground">{t('broadcast.defaultHint', 'Every new vehicle texts these people until you give it its own list.')}</p>
                            <RecipientListEditor
                                value={settings.broadcast_default || []}
                                max={settings.max_recipients || 10}
                                onChange={(rows) => setSettings((s) => ({ ...s, broadcast_default: rows }))}
                                onValidityChange={setDefPhonesOk}
                                testid="uv-settings-recipients"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('broadcast.templateTitle', 'Alert message')}</Label>
                            <p className="text-xs text-muted-foreground">{t('broadcast.templateHint', 'Use {{vehicle}}, {{stock}}, {{customer}}, {{dealership}} and {{link}} — the link opens that vehicle\'s card.')}</p>
                            {['tap', 'inquiry'].map((kind) => (
                                <div key={kind} className="space-y-1.5">
                                    <p className="text-xs font-semibold">{kind === 'tap' ? t('broadcast.kindTap', 'When a vehicle is scanned') : t('broadcast.kindInquiry', 'When a new enquiry arrives')}</p>
                                    {['en', 'fr', 'es'].map((lg) => (
                                        <Input key={lg} value={(settings.broadcast_template?.[kind] || {})[lg] || ''}
                                            placeholder={lg.toUpperCase()}
                                            onChange={(e) => setSettings((s) => ({
                                                ...s,
                                                broadcast_template: {
                                                    ...(s.broadcast_template || {}),
                                                    [kind]: { ...((s.broadcast_template || {})[kind] || {}), [lg]: e.target.value },
                                                },
                                            }))}
                                            data-testid={`uv-settings-tpl-${kind}-${lg}`} />
                                    ))}
                                </div>
                            ))}
                        </div>
                        {/* Buyer-facing look — ORG-scoped, separate from the
                            portal's per-user theme. Existing orgs stay dark. */}
                        <div className="space-y-2">
                            <Label>{t('buyerTheme.label', 'Buyer page appearance')}</Label>
                            <p className="text-xs text-muted-foreground">{t('buyerTheme.hint', 'How your vehicle pages look to buyers. The demo always stays dark.')}</p>
                            <div className="grid grid-cols-2 gap-2" data-testid="uv-settings-buyer-theme">
                                {[['dark', t('buyerTheme.dark', 'Night (dark)')], ['light', t('buyerTheme.light', 'Day (light)')]].map(([key, label]) => (
                                    <button key={key} type="button" onClick={() => setSettings((s) => ({ ...s, buyer_theme: key }))}
                                        data-testid={`uv-settings-theme-${key}`}
                                        className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${(settings.buyer_theme || 'dark') === key ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-background text-muted-foreground hover:bg-accent'}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* F&I desk — each function independently switchable. */}
                        <div className="space-y-2">
                            <Label>{t('fi.settingsTitle', 'Finance & Insurance desk')}</Label>
                            <p className="text-xs text-muted-foreground">{t('fi.settingsHint', 'Turn off anything you do not offer — buyers simply never see that section.')}</p>
                            {[['fi_financing_enabled', t('fi.toggleFinancing', 'Financing')],
                              ['fi_warranty_enabled', t('fi.toggleWarranty', 'Extended warranty')],
                              ['fi_insurance_enabled', t('fi.toggleInsurance', 'Insurance quotes')],
                              ['fi_trade_in_enabled', t('fi.toggleTradeIn', 'Trade-in appraisals')],
                              ['action_noise_enabled', t('tactile.orgToggle', 'Buyer action sounds')],
                              ['payment_estimate_enabled', t('fi.togglePaymentEstimate', 'Show estimated payment on vehicle pages')]].map(([key, label]) => (
                                <label key={key} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 cursor-pointer">
                                    <span className="text-sm">{label}</span>
                                    <Switch checked={!!settings[key]} onCheckedChange={(c) => setSettings((s) => ({ ...s, [key]: c }))}
                                        data-testid={`uv-settings-${key.replace(/_/g, '-')}`} />
                                </label>
                            ))}
                        </div>
                        {/* Per-dealership insurance broker (never platform-wide). */}
                        <div className="space-y-2">
                            <Label>{t('fi.brokerTitle', 'Your insurance broker')}</Label>
                            <p className="text-xs text-muted-foreground">{t('fi.brokerHint', 'Insurance quote requests go to this person. Without a broker, the insurance button stays hidden.')}</p>
                            <Input value={settings.insurance_broker?.name || ''} placeholder={t('fi.brokerName', 'Broker name')}
                                onChange={(e) => setSettings((s) => ({ ...s, insurance_broker: { ...(s.insurance_broker || {}), name: e.target.value } }))}
                                data-testid="uv-settings-broker-name" />
                            <Input value={settings.insurance_broker?.email || ''} placeholder={t('fi.brokerEmail', 'Broker email')}
                                onChange={(e) => setSettings((s) => ({ ...s, insurance_broker: { ...(s.insurance_broker || {}), email: e.target.value } }))}
                                data-testid="uv-settings-broker-email" />
                            <Input value={settings.insurance_broker?.phone || ''} placeholder={t('fi.brokerPhone', 'Broker mobile')}
                                onChange={(e) => setSettings((s) => ({ ...s, insurance_broker: { ...(s.insurance_broker || {}), phone: e.target.value } }))}
                                data-testid="uv-settings-broker-phone" />
                        </div>
                        {/* Dedicated F&I / Trade desk fan-out. */}
                        <div className="space-y-2">
                            <Label>{t('fi.tradeDeskTitle', 'F&I / Trade desk alerts')}</Label>
                            <p className="text-xs text-muted-foreground">{t('fi.tradeDeskHint', 'Trade-in appraisals and financing applications text these people.')}</p>
                            <RecipientListEditor
                                value={settings.fi_trade_recipients || []}
                                max={settings.max_recipients || 10}
                                onChange={(rows) => setSettings((s) => ({ ...s, fi_trade_recipients: rows }))}
                                testid="uv-settings-trade-recipients"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('offerDeposit.settingsEtransfer', 'E-transfer email')}</Label>
                            <Input value={settings.etransfer_email || ''} placeholder="deposits@dealership.com"
                                onChange={(e) => setSettings((s) => ({ ...s, etransfer_email: e.target.value }))}
                                data-testid="uv-settings-etransfer" />
                        </div>
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={() => setSettingsOpen(false)} data-testid="uv-settings-cancel">{t('common.cancel', 'Cancel')}</Button>
                        <Button onClick={saveSettings} disabled={settingsBusy || !defPhonesOk} data-testid="uv-settings-save">{t('offerDeposit.settingsSave', 'Save settings')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Per-vehicle broadcast list */}
            <Dialog open={!!bc} onOpenChange={(o) => !o && setBc(null)}>
                <DialogContent className="sm:max-w-lg" data-testid="uv-broadcast-modal">
                    <DialogHeader>
                        <DialogTitle>{t('broadcast.title', 'Who gets texted about this vehicle')}</DialogTitle>
                        <DialogDescription>
                            {bc ? `${bc.vehicle.year || ''} ${bc.vehicle.make} ${bc.vehicle.model} · #${bc.vehicle.stock_number}` : ''}
                        </DialogDescription>
                    </DialogHeader>
                    {bc && (
                        <div className="space-y-4">
                            <label className="flex items-start gap-2 text-sm cursor-pointer">
                                <Checkbox checked={bc.muted} onCheckedChange={(c) => setBc((p) => ({ ...p, muted: !!c }))} data-testid="uv-broadcast-mute" />
                                <span>
                                    <span className="font-medium">{t('broadcast.mute', 'Mute this vehicle')}</span>
                                    <span className="block text-xs text-muted-foreground">{t('broadcast.muteHint', 'Stops scan and new-enquiry texts. A conversation someone already owns keeps notifying them.')}</span>
                                </span>
                            </label>
                            <label className="flex items-start gap-2 text-sm cursor-pointer">
                                <Checkbox checked={bc.uses_default} disabled={bc.muted}
                                    onCheckedChange={(c) => setBc((p) => ({ ...p, uses_default: !!c }))}
                                    data-testid="uv-broadcast-use-default" />
                                <span>
                                    <span className="font-medium">{t('broadcast.useDefault', 'Use the dealership default list')}</span>
                                    <span className="block text-xs text-muted-foreground">
                                        {(bc.org_default || []).map((r) => r.name || r.phone).join(', ') || t('broadcast.noDefault', 'No default list set yet')}
                                    </span>
                                </span>
                            </label>
                            {!bc.uses_default && (
                                <RecipientListEditor
                                    value={bc.rows} max={bc.max} disabled={bc.muted}
                                    onChange={(rows) => setBc((p) => ({ ...p, rows }))}
                                    onValidityChange={setBcPhonesOk}
                                    testid="uv-broadcast-recipients"
                                />
                            )}
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={() => setBc(null)} data-testid="uv-broadcast-cancel">{t('common.cancel', 'Cancel')}</Button>
                        <Button onClick={saveBroadcast} disabled={bcBusy || !bcPhonesOk} data-testid="uv-broadcast-save">{t('common.save', 'Save')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Walkthrough offer — appears EVERY time (no persistence) */}
            <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
                <DialogContent className="sm:max-w-md" data-testid="uv-walkthrough-offer">
                    <DialogHeader>
                        <DialogTitle>{t('usedVehicles.offerTitle', 'Would you like a step-by-step walkthrough?')}</DialogTitle>
                        <DialogDescription>{t('usedVehicles.offerBody', "We'll walk you through adding this vehicle and putting a Tap Card on it — right inside the real form.")}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={() => { setOfferOpen(false); openWizard(false); }} data-testid="uv-do-myself">{t('usedVehicles.doMyself', "I'll Do It Myself")}</Button>
                        <Button onClick={() => { setOfferOpen(false); openWizard(true); }} data-testid="uv-walk-me">{t('usedVehicles.walkMe', 'Walk Me Through It')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Vehicle wizard (shared form; guided = one step at a time, manual = all at once) */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="uv-vehicle-modal">
                    <DialogHeader>
                        <DialogTitle>{editing ? t('usedVehicles.editTitle', 'Edit Vehicle') : t('usedVehicles.addTitle', 'Add Used Vehicle')} <span className="text-muted-foreground font-normal text-sm">· {orgName(filterOrg)}</span></DialogTitle>
                        <DialogDescription className="sr-only">{t('usedVehicles.subtitle', 'Manage the vehicles on your YulOnTap Tap Cards')}</DialogDescription>
                        {guided && (
                            <div className="mt-1 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2" data-testid="uv-coach">
                                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300" data-testid="uv-coach-title">
                                    {t('usedVehicles.stepLabel', 'Step')} {step + 1}/{STEPS.length} — {coach[STEPS[step]][0]}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5" data-testid="uv-coach-hint">{coach[STEPS[step]][1]}</p>
                            </div>
                        )}
                    </DialogHeader>

                    <div className="space-y-5">
                        {showSection('stock') && (
                            <div className="space-y-3">
                                <div>
                                    <Label>{t('usedVehicles.f.stock', 'Stock #')}</Label>
                                    <Input value={form.stock_number} onChange={(e) => set('stock_number', e.target.value)} placeholder="B12345" data-testid="uv-field-stock" />
                                </div>
                                <div>
                                    <Label>{t('usedVehicles.f.vin', 'VIN (Vehicle Identification Number)')}</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={form.vin}
                                            onChange={(e) => { set('vin', e.target.value.toUpperCase()); if (vinStatus) setVinStatus(null); }}
                                            placeholder="1HGCM82633A004352"
                                            maxLength={20}
                                            className="uppercase tracking-wide"
                                            data-testid="uv-field-vin"
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={runVinLookup}
                                            disabled={vinStatus?.kind === 'loading' || !(form.vin || '').trim()}
                                            data-testid="uv-vin-lookup"
                                            title={t('usedVehicles.vinLookupBtn', 'Look up VIN')}
                                        >
                                            {vinStatus?.kind === 'loading'
                                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                                : editing ? <RefreshCw className="w-4 h-4" /> : <Search className="w-4 h-4" />}
                                            <span className="ml-2 hidden sm:inline">
                                                {editing ? t('usedVehicles.vinRefresh', 'Refresh VIN Data') : t('usedVehicles.vinLookupBtn', 'Look up VIN')}
                                            </span>
                                        </Button>
                                    </div>
                                    {vinStatus && (
                                        <p
                                            className={`text-xs mt-1.5 flex items-center gap-1.5 ${vinStatus.kind === 'error' ? 'text-red-500' : vinStatus.kind === 'found' ? 'text-emerald-600' : vinStatus.kind === 'loading' ? 'text-muted-foreground' : 'text-amber-600'}`}
                                            data-testid="uv-vin-status"
                                        >
                                            {vinStatus.kind === 'loading' && <Loader2 className="w-3 h-3 animate-spin" />}
                                            {vinStatus.kind === 'found' && <CheckCircle2 className="w-3 h-3" />}
                                            {vinStatus.msg}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                        {showSection('model') && (
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>{t('usedVehicles.f.make', 'Make')}</Label><Input value={form.make} onChange={(e) => set('make', e.target.value)} placeholder="BMW" data-testid="uv-field-make" /></div>
                                <div><Label>{t('usedVehicles.f.model', 'Model')}</Label><Input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="X5" data-testid="uv-field-model" /></div>
                                <div><Label>{t('usedVehicles.f.year', 'Year')}</Label><Input value={form.year} onChange={(e) => set('year', e.target.value)} placeholder="2024" data-testid="uv-field-year" /></div>
                                <div><Label>{t('usedVehicles.f.trim', 'Trim')}</Label><Input value={form.trim} onChange={(e) => set('trim', e.target.value)} placeholder="xDrive40i" data-testid="uv-field-trim" /></div>
                            </div>
                        )}
                        {showSection('details') && (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>{t('usedVehicles.f.mileage', 'Mileage')}</Label><Input value={form.mileage} onChange={(e) => set('mileage', e.target.value)} placeholder="139,000 km" data-testid="uv-field-mileage" /></div>
                                    <div><Label>{t('usedVehicles.f.colour', 'Colour')}</Label><Input value={form.colour} onChange={(e) => set('colour', e.target.value)} placeholder="Black" data-testid="uv-field-colour" /></div>
                                </div>
                                <div><Label>{t('usedVehicles.f.price', 'Asking Price')}</Label><CurrencyInput value={form.asking_price} onChange={(v) => set('asking_price', v)} placeholder="$54,995" data-testid="uv-field-price" /></div>
                                <div className="pt-1">
                                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{t('vehicleExp.specifications', 'Specifications')} <span className="normal-case">({t('common.optional', 'optional')})</span></p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><Label>{t('vehicleExp.spec.bodyStyle', 'Body Style')}</Label><Input value={form.body_style} onChange={(e) => set('body_style', e.target.value)} placeholder="SUV" data-testid="uv-field-body_style" /></div>
                                        <div><Label>{t('vehicleExp.spec.engine', 'Engine')}</Label><Input value={form.engine} onChange={(e) => set('engine', e.target.value)} placeholder="2.0L Turbo I4" data-testid="uv-field-engine" /></div>
                                        <div><Label>{t('vehicleExp.spec.drivetrain', 'Drivetrain')}</Label><Input value={form.drivetrain} onChange={(e) => set('drivetrain', e.target.value)} placeholder="AWD" data-testid="uv-field-drivetrain" /></div>
                                        <div><Label>{t('vehicleExp.spec.transmission', 'Transmission')}</Label><Input value={form.transmission} onChange={(e) => set('transmission', e.target.value)} placeholder="8-speed automatic" data-testid="uv-field-transmission" /></div>
                                        <div><Label>{t('vehicleExp.spec.fuelType', 'Fuel Type')}</Label><Input value={form.fuel_type} onChange={(e) => set('fuel_type', e.target.value)} placeholder="Gasoline" data-testid="uv-field-fuel_type" /></div>
                                        <div><Label>{t('vehicleExp.spec.fuelEconomy', 'Fuel Economy')}</Label><Input value={form.fuel_economy} onChange={(e) => set('fuel_economy', e.target.value)} placeholder="9.5 L/100km" data-testid="uv-field-fuel_economy" /></div>
                                        <div><Label>{t('vehicleExp.spec.doors', 'Doors')}</Label><Input value={form.doors} onChange={(e) => set('doors', e.target.value)} placeholder="4" data-testid="uv-field-doors" /></div>
                                        <div><Label>{t('vehicleExp.spec.seating', 'Seating')}</Label><Input value={form.seating} onChange={(e) => set('seating', e.target.value)} placeholder="5" data-testid="uv-field-seating" /></div>
                                    </div>
                                </div>
                                <div className="pt-1">
                                    <Label>{t('usedVehicles.f.features', 'Vehicle Features')}</Label>
                                    <p className="text-xs text-muted-foreground mb-2">{t('usedVehicles.featuresHint', 'Select all that apply. Auto-filled from VIN when detailed factory data is available on your plan.')}</p>
                                    <div className="flex flex-wrap gap-2" data-testid="uv-features">
                                        {FEATURE_OPTIONS.map((f) => {
                                            const on = form.features.includes(f);
                                            return (
                                                <button
                                                    type="button"
                                                    key={f}
                                                    onClick={() => toggleFeature(f)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? 'bg-amber-500 text-white border-amber-500' : 'bg-transparent text-muted-foreground border-border hover:border-amber-500'}`}
                                                    data-testid={`uv-feature-${f.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                                                    aria-pressed={on}
                                                >
                                                    {on && <CheckCircle2 className="w-3 h-3 inline mr-1 -mt-0.5" />}{f}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                {/* Repeatable Extended Warranty plans (Part 2B).
                                    No plans = no Warranty button for this vehicle. */}
                                <div className="space-y-2 pt-1" data-testid="uv-warranty-plans">
                                    <Label>{t('warrantyPlans.title', 'Warranty options')}</Label>
                                    <p className="text-xs text-muted-foreground">{t('warrantyPlans.hint', 'Leave empty and buyers see no warranty button on this vehicle.')}</p>
                                    {(form.warranty_plans || []).map((plan, i) => (
                                        <div key={i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`uv-warranty-plan-${i}`}>
                                            <div className="flex flex-wrap gap-2">
                                                {['1 year', '3 year', '5 year', '7 year', '10 year'].map((preset) => (
                                                    <button key={preset} type="button"
                                                        onClick={() => setPlan(i, 'duration', preset)}
                                                        className={`px-3 h-9 rounded-full border text-xs font-semibold ${plan.duration === preset ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent'}`}
                                                        data-testid={`uv-warranty-preset-${i}-${preset.replace(/\s/g, '')}`}>
                                                        {preset}
                                                    </button>
                                                ))}
                                            </div>
                                            <Input value={plan.duration || ''} onChange={(e) => setPlan(i, 'duration', e.target.value)}
                                                placeholder={t('warrantyPlans.duration', 'Duration')} data-testid={`uv-warranty-duration-${i}`} />
                                            <Input value={plan.coverage || ''} onChange={(e) => setPlan(i, 'coverage', e.target.value)}
                                                placeholder={t('warrantyPlans.coverage', 'Coverage type')} data-testid={`uv-warranty-coverage-${i}`} />
                                            <Input value={plan.notes || ''} onChange={(e) => setPlan(i, 'notes', e.target.value)}
                                                placeholder={t('warrantyPlans.notes', 'Notes')} data-testid={`uv-warranty-notes-${i}`} />
                                            <Button type="button" variant="ghost" size="sm" className="text-red-600"
                                                onClick={() => setForm((f) => ({ ...f, warranty_plans: f.warranty_plans.filter((_, x) => x !== i) }))}
                                                data-testid={`uv-warranty-remove-${i}`}>
                                                {t('warrantyPlans.remove', 'Remove')}
                                            </Button>
                                        </div>
                                    ))}
                                    <Button type="button" variant="outline" size="sm"
                                        onClick={() => setForm((f) => ({ ...f, warranty_plans: [...(f.warranty_plans || []), { duration: '', coverage: '', notes: '' }] }))}
                                        data-testid="uv-warranty-add">
                                        {t('warrantyPlans.add', '+ Add another plan')}
                                    </Button>
                                </div>
                                {/* 9B — per-vehicle Test Drive Gift. Inherit keeps this
                                    car on whatever the dealership default is. */}
                                <div className="space-y-2 pt-1" data-testid="uv-test-drive-gift">
                                    <Label>{t('gift.toggleTitle', 'Test Drive Gift')}</Label>
                                    <p className="text-xs text-muted-foreground">{t('gift.vehicleHint', 'Only sent once a test drive of THIS car is marked complete.')}</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[[null, t('gift.inherit', 'Dealership default')], [true, t('common.on', 'On')], [false, t('common.off', 'Off')]].map(([val, label]) => (
                                            <button type="button" key={String(val)}
                                                onClick={() => set('test_drive_gift_enabled', val)}
                                                data-testid={`uv-gift-${val === null ? 'inherit' : val ? 'on' : 'off'}`}
                                                className={`h-10 rounded-lg border text-xs font-semibold transition-colors ${form.test_drive_gift_enabled === val ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-background text-muted-foreground hover:bg-accent'}`}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-2 pt-1">
                                    <Input value={form.field_notes.mileage} onChange={(e) => setNote('mileage', e.target.value)} placeholder={t('usedVehicles.noteMileage', 'Note (optional): e.g. Mostly highway driven')} data-testid="uv-note-mileage" />
                                    <Input value={form.field_notes.colour} onChange={(e) => setNote('colour', e.target.value)} placeholder={t('usedVehicles.noteColour', 'Note (optional): e.g. Original factory finish')} data-testid="uv-note-colour" />
                                    <Input value={form.field_notes.price} onChange={(e) => setNote('price', e.target.value)} placeholder={t('usedVehicles.notePrice', 'Note (optional): e.g. Financing available')} data-testid="uv-note-price" />
                                </div>
                            </div>
                        )}
                        {showSection('photos') && (
                            <div>
                                <Label>{t('usedVehicles.f.photos', 'Photos')}</Label>
                                <p className="text-xs text-muted-foreground mb-2">{t('usedVehicles.photosHint', 'Upload the dealership’s own photos. The first photo is the main image shown to customers. VIN lookups never change your photos.')}</p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {form.photos.map((p, i) => (
                                        <div key={i} className={`relative w-20 h-20 rounded-lg overflow-hidden border ${i === 0 ? 'border-amber-500 ring-1 ring-amber-500' : 'border-border'}`} data-testid={`uv-photo-thumb-${i}`}>
                                            <img src={p} alt="" className="w-full h-full object-cover" />
                                            {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-amber-500 text-white text-[9px] text-center font-semibold py-0.5" data-testid="uv-photo-main-badge">{t('usedVehicles.mainPhoto', 'Main')}</span>}
                                            {i !== 0 && (
                                                <button type="button" onClick={() => set('photos', [form.photos[i], ...form.photos.filter((_, idx) => idx !== i)])} className="absolute bottom-0.5 left-0.5 bg-black/60 rounded px-1 text-[9px] text-white hover:bg-amber-500" data-testid={`uv-photo-makemain-${i}`} title={t('usedVehicles.setMain', 'Set as main')}>{t('usedVehicles.setMain', 'Set as main')}</button>
                                            )}
                                            <button onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5" data-testid={`uv-photo-remove-${i}`}><X className="w-3 h-3 text-white" /></button>
                                        </div>
                                    ))}
                                    <label className="w-20 h-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-amber-500 transition-colors" data-testid="uv-photo-add">
                                        <ImagePlus className="w-5 h-5 text-muted-foreground" />
                                        <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="uv-photo-input" />
                                    </label>
                                </div>
                                {uploading && <p className="text-xs text-muted-foreground mt-1">{t('usedVehicles.uploading', 'Uploading…')}</p>}
                            </div>
                        )}
                        {showSection('studio') && (
                            <div className="space-y-3" data-testid="uv-studio">
                                <div>
                                    <Label>{t('usedVehicles.f.studio', 'Studio / Wallpaper')}</Label>
                                    <p className="text-xs text-muted-foreground">{t('usedVehicles.studioHint', 'Optional presentation layer. Adds a backdrop behind your main photo. Your uploaded photos are never changed or replaced.')}</p>
                                </div>
                                {/* Live preview: main dealer photo over the chosen backdrop */}
                                <div className="rounded-xl overflow-hidden border border-border">
                                    <div className="relative aspect-video flex items-center justify-center bg-neutral-900" style={wallpaperBgStyle(form) || undefined} data-testid="uv-studio-preview">
                                        {form.photos?.[0] ? (
                                            hasWallpaper(form) ? (
                                                <div className={`m-4 rounded-lg overflow-hidden ${cardSurfaceClass(form.card_style, 'dark')} p-1.5`}>
                                                    <img src={form.photos[0]} alt="" className="max-h-40 w-auto object-contain rounded" />
                                                </div>
                                            ) : (
                                                <img src={form.photos[0]} alt="" className="w-full h-full object-cover" />
                                            )
                                        ) : (
                                            <span className="text-xs text-neutral-500">{t('usedVehicles.studioNoPhoto', 'Add a photo first to see the preview')}</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-medium mb-1.5">{t('usedVehicles.studioBackdrop', 'Backdrop')}</p>
                                    <div className="flex flex-wrap gap-2" data-testid="uv-studio-presets">
                                        <button type="button" onClick={clearWallpaper} className={`px-3 py-1.5 rounded-lg text-xs border ${!hasWallpaper(form) ? 'border-amber-500 ring-1 ring-amber-500' : 'border-border'}`} data-testid="uv-wp-none">{t('usedVehicles.studioNone', 'None')}</button>
                                        {WALLPAPER_PRESETS.map((p) => (
                                            <button type="button" key={p.key} onClick={() => pickPreset(p.key)} className={`w-14 h-9 rounded-lg border ${form.wallpaper_preset === p.key ? 'border-amber-500 ring-1 ring-amber-500' : 'border-border'}`} style={{ backgroundImage: p.css }} title={p.label} data-testid={`uv-wp-${p.key}`} />
                                        ))}
                                        <label className="px-3 py-1.5 rounded-lg text-xs border border-dashed border-border cursor-pointer hover:border-amber-500 flex items-center gap-1" data-testid="uv-wp-upload">
                                            <ImagePlus className="w-3.5 h-3.5" />{wpUploading ? t('usedVehicles.uploading', 'Uploading…') : t('usedVehicles.studioUpload', 'Upload')}
                                            <input type="file" accept="image/*" className="hidden" onChange={onWallpaperUpload} data-testid="uv-wp-file" />
                                        </label>
                                    </div>
                                    {form.wallpaper_url && (
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{t('usedVehicles.studioCustom', 'Custom wallpaper')}</span>
                                            <button type="button" onClick={clearWallpaper} className="text-xs text-red-500" data-testid="uv-wp-remove">{t('common.remove', 'Remove')}</button>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs font-medium mb-1.5">{t('usedVehicles.studioFrame', 'Photo frame')}</p>
                                    <div className="flex gap-2" data-testid="uv-studio-frame">
                                        {CARD_STYLES.map((s) => (
                                            <button type="button" key={s} onClick={() => set('card_style', s)} className={`px-3 py-1.5 rounded-lg text-xs capitalize border ${form.card_style === s ? 'bg-amber-500 text-white border-amber-500' : 'border-border text-muted-foreground'}`} data-testid={`uv-frame-${s}`}>{t(`usedVehicles.frame.${s}`, s)}</button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        {showSection('carfax') && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" />{t('usedVehicles.f.carfax', 'CARFAX report')}</Label>
                                    <Switch checked={form.carfax_enabled} onCheckedChange={(v) => set('carfax_enabled', v)} data-testid="uv-carfax-toggle" />
                                </div>
                                {form.carfax_enabled && (
                                    <Input value={form.carfax_url} onChange={(e) => set('carfax_url', e.target.value)} placeholder="https://www.carfax.ca/vhr/…" data-testid="uv-field-carfax-url" />
                                )}
                                <div className="flex items-center justify-between pt-2 border-t border-border">
                                    <Label className="flex items-center gap-2">{t('usedVehicles.f.offer', 'Promotion')}</Label>
                                    <Switch checked={form.special_offer_enabled} onCheckedChange={(v) => set('special_offer_enabled', v)} data-testid="uv-offer-toggle" />
                                </div>
                                {form.special_offer_enabled && (
                                    <>
                                        <Input value={form.special_offer_url} onChange={(e) => set('special_offer_url', e.target.value)} placeholder={t('usedVehicles.offerUrlPh', 'Gift Generator URL (https://…)')} data-testid="uv-field-offer-url" />
                                        <Input value={form.special_offer_label} onChange={(e) => set('special_offer_label', e.target.value)} placeholder={t('usedVehicles.offerLabelPh', 'Button label (optional), e.g. $500 toward Service')} data-testid="uv-field-offer-label" />
                                    </>
                                )}
                            </div>
                        )}
                        {showSection('card') && (
                            <div>
                                <Label className="flex items-center gap-2"><Tag className="w-4 h-4 text-amber-500" />{t('usedVehicles.f.card', 'Tap Card')}</Label>
                                <Select value={selectedCard} onValueChange={setSelectedCard}>
                                    <SelectTrigger className="mt-1" data-testid="uv-card-select"><SelectValue placeholder={t('usedVehicles.selectCard', 'Select a Tap Card')} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">{t('usedVehicles.noCardOption', 'No card yet')}</SelectItem>
                                        {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.card_id}{c.name ? ` — ${c.name}` : ''}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {showSection('review') && (
                            <div className="rounded-lg bg-muted/50 p-4 text-sm space-y-1" data-testid="uv-review">
                                <p className="font-semibold flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-emerald-600" />{form.year} {form.make} {form.model}</p>
                                <p className="text-muted-foreground">{t('usedVehicles.stockShort', 'Stock')} #{form.stock_number || '—'} · {form.mileage || '—'} · {form.colour || '—'} · {formatMoney(form.asking_price) || '—'}</p>
                                <p className="text-muted-foreground">{t('usedVehicles.f.photos', 'Photos')}: {form.photos.length} · CARFAX: {form.carfax_enabled ? t('usedVehicles.on', 'ON') : t('usedVehicles.off', 'OFF')} · {t('usedVehicles.f.card', 'Tap Card')}: {selectedCard === 'none' ? t('usedVehicles.noCard', 'No Tap Card') : (cards.find((c) => c.id === selectedCard)?.card_id || '—')}</p>
                            </div>
                        )}
                    </div>

                    <DialogFooter className="gap-2 sm:gap-2">
                        {guided ? (
                            <>
                                {step > 0 && <Button variant="outline" onClick={back} data-testid="uv-step-back"><ChevronLeft className="w-4 h-4 mr-1" />{t('common.back', 'Back')}</Button>}
                                {step < STEPS.length - 1
                                    ? <Button onClick={next} data-testid="uv-step-next">{t('common.next', 'Next')}<ChevronRight className="w-4 h-4 ml-1" /></Button>
                                    : <>
                                        <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="uv-preview"><Eye className="w-4 h-4 mr-1.5" />{t('usedVehicles.preview', 'Preview')}</Button>
                                        <Button onClick={save} disabled={saving} data-testid="uv-step-save"><CheckCircle2 className="w-4 h-4 mr-1.5" />{saving ? t('common.saving', 'Saving…') : t('usedVehicles.saveVehicle', 'Save Vehicle')}</Button>
                                    </>}
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="uv-cancel">{t('common.cancel', 'Cancel')}</Button>
                                <Button variant="outline" onClick={() => setPreviewOpen(true)} data-testid="uv-preview"><Eye className="w-4 h-4 mr-1.5" />{t('usedVehicles.preview', 'Preview')}</Button>
                                <Button onClick={save} disabled={saving} data-testid="uv-step-save"><CheckCircle2 className="w-4 h-4 mr-1.5" />{saving ? t('common.saving', 'Saving…') : t('usedVehicles.saveVehicle', 'Save Vehicle')}</Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Reassign Tag — fast, high-frequency dealer workflow */}
            <Dialog open={!!reassignV} onOpenChange={(o) => !o && setReassignV(null)}>
                <DialogContent className="sm:max-w-md" data-testid="uv-reassign-modal">
                    <DialogHeader>
                        <DialogTitle>{t('usedVehicles.reassignTitle', 'Reassign Tap Card')}</DialogTitle>
                        <DialogDescription>{t('usedVehicles.reassignBody', 'Move a physical Tap Card onto this vehicle. The tag itself is never rewritten — the next tap resolves to this vehicle.')}</DialogDescription>
                    </DialogHeader>
                    {reassignV && (
                        <div className="space-y-4">
                            <div className="rounded-lg bg-muted/50 p-3 text-sm">
                                <p className="font-semibold text-slate-900 dark:text-white">{reassignV.year} {reassignV.make} {reassignV.model}</p>
                                <p className="text-muted-foreground">{t('usedVehicles.stockShort', 'Stock')} #{reassignV.stock_number}</p>
                                <p className="text-muted-foreground mt-1">{t('usedVehicles.currentTag', 'Current Tap Card')}: <span className="font-medium text-foreground">{reassignV.assigned_card?.card_id || t('usedVehicles.noCard', 'No Tap Card')}</span></p>
                            </div>
                            <div>
                                <Label>{t('usedVehicles.newTag', 'New Tap Card')}</Label>
                                <Select value={reassignSel} onValueChange={setReassignSel}>
                                    <SelectTrigger className="mt-1" data-testid="uv-reassign-select"><SelectValue placeholder={t('usedVehicles.selectCard', 'Select a Tap Card')} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">{t('usedVehicles.noCardOption', 'No card yet')}</SelectItem>
                                        {reassignCards.map((c) => <SelectItem key={c.id} value={c.id}>{c.card_id}{c.name ? ` — ${c.name}` : ''}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={() => setReassignV(null)} data-testid="uv-reassign-cancel">{t('common.cancel', 'Cancel')}</Button>
                        <Button onClick={confirmReassign} disabled={reassigning} data-testid="uv-reassign-confirm">
                            <Link2 className="w-4 h-4 mr-1.5" />{reassigning ? t('common.saving', 'Saving…') : t('usedVehicles.reassignConfirm', 'Reassign Tag')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Staff PREVIEW — the exact premium showroom, using UNSAVED values. Does not save. */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="p-0 max-w-md w-full max-h-[92vh] overflow-y-auto bg-neutral-950 border-neutral-800" data-testid="uv-preview-modal">
                    <DialogHeader className="sr-only">
                        <DialogTitle>{t('usedVehicles.previewTitle', 'Customer preview')}</DialogTitle>
                        <DialogDescription>{t('usedVehicles.previewDesc', 'Exactly what the customer will see')}</DialogDescription>
                    </DialogHeader>
                    <div className="sticky top-0 z-40 flex items-center justify-between px-4 py-2 bg-amber-500 text-black text-xs font-bold">
                        <span data-testid="uv-preview-banner">{t('usedVehicles.previewBanner', 'PREVIEW — not saved')}</span>
                        <button onClick={() => setPreviewOpen(false)} data-testid="uv-preview-close"><X className="w-4 h-4" /></button>
                    </div>
                    <VehicleShowroom
                        vehicle={form}
                        dealership={orgName(filterOrg)}
                        salesStatus={{ open: true, today: { open: '09:00', close: '18:00' }, timezone: 'preview' }}
                        preview
                        onContact={() => toast.info(t('usedVehicles.previewAction', 'Buttons are live on the real page'))}
                        onTestDrive={() => toast.info(t('usedVehicles.previewAction', 'Buttons are live on the real page'))}
                        onSave={() => toast.info(t('usedVehicles.previewAction', 'Buttons are live on the real page'))}
                        onCompare={() => toast.info(t('usedVehicles.previewAction', 'Buttons are live on the real page'))}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
